import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCollegeContext } from '../../contexts/CollegeContext';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { supabase } from '../../lib/supabase';
import { getStudentType, calculateTimelineMessage } from '../../utils/collegeTimelineCalculator';
import { 
  ArrowLeft, 
  ArrowRight, 
  Copy, 
  CheckCircle, 
  Zap, 
  Target,
  Save,
  RefreshCw,
  FileText,
  Brain,
  Sparkles,
  MessageCircle,
  Settings,
  TrendingUp
} from 'lucide-react';

const DynamicGuidanceGenerator = () => {
  const navigate = useNavigate();
  const { currentUser, familyUsers, family } = useFamilyContext();
  const { updateMasterStrategy } = useCollegeContext();
  
  const [loading, setLoading] = useState(true);
  const [contextSummary, setContextSummary] = useState('');
  const [questionSet, setQuestionSet] = useState([]);
  const [dynamicQuestionGuidance, setDynamicQuestionGuidance] = useState('');
  const [guidanceRefinementOptions, setGuidanceRefinementOptions] = useState([]);
  const [personalityProfile, setPersonalityProfile] = useState({});
  const [academicFocus, setAcademicFocus] = useState({});
  const [studentProfile, setStudentProfile] = useState(null);
  const [copySuccess, setCopySuccess] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPhase3Data();
  }, [currentUser, familyUsers, family]);

  const loadPhase3Data = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      
      // Get the selected student auth_user_id from localStorage (updated approach)
      const savedStudentId = localStorage.getItem('college-selected-student-id');
      
      if (!savedStudentId) {
        console.error('No valid student user ID found. Please ensure a student is selected.');
        setLoading(false);
        return;
      }
      
      // The savedStudentId is now the auth_user_id we need
      const userIdForQuery = savedStudentId;
      
      console.log('🔍 DynamicGuidanceGenerator: Loading Phase 3 data for auth_user_id:', userIdForQuery);
      console.log('🔍 DynamicGuidanceGenerator: savedStudentId from localStorage:', savedStudentId);
      
      const { data: profileData, error } = await supabase
        .from('student_academic_profiles')
        .select(`
          strategic_context_summary,
          initial_question_set,
          dynamic_question_guidance,
          guidance_refinement_options,
          personality_profile,
          assessment_focus_area,
          positioning_narrative,
          assessment_template,
          name,
          grade_level,
          assessment_phase
        `)
        .eq('user_id', userIdForQuery)
        .single();

      console.log('🔍 DynamicGuidanceGenerator: Database query result - error:', error);
      console.log('🔍 DynamicGuidanceGenerator: Database query result - data:', profileData);
      
      if (error) {
        console.error('❌ DynamicGuidanceGenerator: Database error:', error);
        // Don't return here, continue with empty state for now
      }

      if (profileData) {
        setContextSummary(profileData.strategic_context_summary || '');
        setQuestionSet(profileData.initial_question_set || []);
        setDynamicQuestionGuidance(profileData.dynamic_question_guidance || '');
        setGuidanceRefinementOptions(profileData.guidance_refinement_options || []);

        // Load student profile basic info
        setStudentProfile({
          name: profileData.name || 'Student',
          grade_level: profileData.grade_level
        });

        // Load personality profile if available
        if (profileData.personality_profile) {
          try {
            const savedProfile = JSON.parse(profileData.personality_profile);
            setPersonalityProfile(savedProfile);
            console.log('🔍 DynamicGuidanceGenerator: Loaded personality profile:', savedProfile);
          } catch (error) {
            console.log('Error parsing personality profile:', error);
            setPersonalityProfile({});
          }
        }

        // Load academic focus and positioning
        setAcademicFocus({
          focusArea: profileData.assessment_focus_area || 'engineering',
          positioningNarrative: profileData.positioning_narrative || 'institutional_catalyst',
          assessmentTemplate: profileData.assessment_template || 'engineering_institutional_catalyst'
        });
        console.log('🔍 DynamicGuidanceGenerator: Loaded academic focus:', {
          focusArea: profileData.assessment_focus_area,
          positioningNarrative: profileData.positioning_narrative,
          assessmentTemplate: profileData.assessment_template
        });
        
        console.log('✅ DynamicGuidanceGenerator: Loaded Phase 3 data:', profileData);
        console.log('🔍 DynamicGuidanceGenerator: Question set length:', profileData.initial_question_set?.length);
        console.log('🔍 DynamicGuidanceGenerator: Question set content:', profileData.initial_question_set);
        console.log('🔍 DynamicGuidanceGenerator: Strategic context length:', profileData.strategic_context_summary?.length);
      } else {
        console.log('⚠️ DynamicGuidanceGenerator: No profile data found for user_id:', userIdForQuery);
        console.log('⚠️ DynamicGuidanceGenerator: This means Phase 2 & 3 data is missing from database');
      }
    } catch (error) {
      console.error('Error loading Phase 3 data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get human-readable labels for personality profile values
  const getPersonalityLabel = (value, category) => {
    const labels = {
      motivation: {
        'achievement': 'wants to excel and be acknowledged',
        'curiosity': 'driven to figure things out and learn',
        'impact': 'motivated by making a difference'
      },
      communication: {
        'data-logic': 'responds to facts, evidence, and logical reasoning',
        'stories-examples': 'learns through narratives and concrete examples',
        'visual-hands-on': 'needs to see it or try it to understand'
      },
      resistance: {
        'authority-skeptical': 'questions "because I said so" reasoning',
        'perfectionist-paralyzed': 'afraid of not doing it perfectly',
        'overwhelm-avoidant': 'shuts down when there\'s too much at once'
      },
      energy: {
        'competition': 'thrives on winning and outperforming others',
        'collaboration': 'energized by working with and helping others',
        'independent-exploration': 'needs space to figure things out alone'
      },
      decision: {
        'research-heavy': 'wants all the data before deciding',
        'intuition-based': 'goes with gut feeling and instinct',
        'consensus-seeking': 'wants input from trusted people'
      },
      feedback: {
        'direct-analytical': 'straightforward, data-based critique',
        'supportive-encouraging': 'positive reinforcement first',
        'challenge-based': 'framed as problems to solve'
      },
      identity: {
        'individual-achiever': 'personal excellence and recognition',
        'community-contributor': 'part of something bigger',
        'system-innovator': 'someone who improves how things work'
      }
    };

    return labels[category]?.[value] || value;
  };

  const generateDynamicQuestionPrompt = () => {
    const prompt = `Create dynamic question guidance for API AI to generate follow-up questions 9-15 based on student responses:

**STRATEGIC CONTEXT:**
${contextSummary}

**INITIAL QUESTIONS 1-8:**
${questionSet.map((q, index) => `${index + 1}. ${q.question}`).join('\n')}

**STUDENT PERSONALITY PROFILE:**
${personalityProfile?.motivationStyle ? `Primary Motivation: ${personalityProfile.motivationStyle} - ${getPersonalityLabel(personalityProfile.motivationStyle, 'motivation')}` : ''}
${personalityProfile?.communicationPreference ? `Communication Style: ${personalityProfile.communicationPreference} - ${getPersonalityLabel(personalityProfile.communicationPreference, 'communication')}` : ''}
${personalityProfile?.resistancePatterns ? `Resistance Patterns: ${personalityProfile.resistancePatterns} - ${getPersonalityLabel(personalityProfile.resistancePatterns, 'resistance')}` : ''}
${personalityProfile?.energySource ? `Energy Source: ${personalityProfile.energySource} - ${getPersonalityLabel(personalityProfile.energySource, 'energy')}` : ''}
${personalityProfile?.decisionMaking ? `Decision Making: ${personalityProfile.decisionMaking} - ${getPersonalityLabel(personalityProfile.decisionMaking, 'decision')}` : ''}
${personalityProfile?.feedbackStyle ? `Feedback Preference: ${personalityProfile.feedbackStyle} - ${getPersonalityLabel(personalityProfile.feedbackStyle, 'feedback')}` : ''}
${personalityProfile?.identityFramework ? `Identity Framework: ${personalityProfile.identityFramework} - ${getPersonalityLabel(personalityProfile.identityFramework, 'identity')}` : ''}

**ACADEMIC FOCUS & POSITIONING:**
${academicFocus?.focusArea ? `Academic Focus: ${academicFocus.focusArea}` : ''}
${academicFocus?.positioningNarrative ? `Positioning Narrative: ${academicFocus.positioningNarrative}` : ''}
${academicFocus?.assessmentTemplate ? `Assessment Framework: ${academicFocus.assessmentTemplate}` : ''}

**TIMELINE CONTEXT:**
${studentProfile?.grade_level ? `Grade Level: ${studentProfile.grade_level}` : ''}
${studentProfile?.grade_level ? `Student Type: ${getStudentType(studentProfile.grade_level)}` : ''}
${studentProfile?.grade_level ? `Timeline: ${calculateTimelineMessage(studentProfile.grade_level).primary}` : ''}

**INSTRUCTIONS:**
Create guidance that will help API AI generate personalized follow-up questions 9-15 based on how ${studentProfile?.name || 'the student'} actually responds to questions 1-8.

The guidance should:
1. Consider the student's personality profile (especially ${personalityProfile?.communicationPreference ? getPersonalityLabel(personalityProfile.communicationPreference, 'communication') : 'their communication style'})
2. Align with their academic focus (${academicFocus?.focusArea || 'their chosen field'}) and positioning narrative (${academicFocus?.positioningNarrative || 'their unique strengths'})
3. Adapt to their motivation style (${personalityProfile?.motivationStyle ? getPersonalityLabel(personalityProfile.motivationStyle, 'motivation') : 'what drives them'})
4. Account for their timeline context: ${studentProfile?.grade_level ? calculateTimelineMessage(studentProfile.grade_level).primary : 'their application timeline'}
5. Respect their resistance patterns (${personalityProfile?.resistancePatterns ? getPersonalityLabel(personalityProfile.resistancePatterns, 'resistance') : 'how they respond to pressure'})
6. Generate questions that feel natural to their identity framework (${personalityProfile?.identityFramework ? getPersonalityLabel(personalityProfile.identityFramework, 'identity') : 'how they see themselves'})

**OUTPUT FORMAT:**
DYNAMIC_QUESTION_GUIDANCE:
[Write detailed instructions for API AI on how to generate questions 9-15. Include:
- What patterns to look for in responses 1-8
- How to build on specific student answers
- What types of follow-up questions would be most revealing
- How to maintain the strategic positioning
- Guidelines for question depth and specificity
- Instructions for adapting to different response styles]

This guidance should be comprehensive enough that API AI can generate highly personalized questions 9-15 without parent input during the assessment.`;

    return prompt;
  };

  // generateSynthesisPrompt moved to Phase 5 (SynthesisInstructions component)

  const copyPromptToClipboard = async () => {
    const prompt = generateDynamicQuestionPrompt();
    try {
      await navigator.clipboard.writeText(prompt);
      setCopySuccess('dynamic_copied');
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setCopySuccess('dynamic_failed');
      setTimeout(() => setCopySuccess(''), 2000);
    }
  };

  const generateGuidanceRefinements = () => {
    const refinements = [
      'Adjust guidance to emphasize intellectual depth and academic rigor in follow-up questions',
      'Refine instructions to highlight character development and personal growth patterns',
      'Modify framework to showcase leadership potential and collaborative impact',
      'Enhance synthesis to connect unique experiences with college differentiation strategy'
    ];
    setGuidanceRefinementOptions(refinements);
  };

  const saveGuidanceFrameworks = async () => {
    if (!currentUser || !dynamicQuestionGuidance.trim()) return;

    setSaving(true);
    try {
      // Use the same user ID logic as loading - FIXED to use auth user ID
      const selectedStudentUserId = localStorage.getItem('selectedStudentUserId');
      const userIdForQuery = selectedStudentUserId || (currentUser.auth_user_id || currentUser.authUserId);
      
      console.log('🔍 DEBUG: Saving guidance frameworks for user:', userIdForQuery);
      
      const { error } = await supabase
        .from('student_academic_profiles')
        .update({
          dynamic_question_guidance: dynamicQuestionGuidance.trim(),
          guidance_refinement_options: guidanceRefinementOptions,
          assessment_phase: 4,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userIdForQuery);

      if (error) {
        console.error('Error saving guidance frameworks:', error);
      } else {
        // Update master strategy
        await updateMasterStrategy({
          dynamic_question_guidance: dynamicQuestionGuidance.trim(),
          guidance_refinement_options: guidanceRefinementOptions,
          phase_4_completed: true,
          updated_at: new Date().toISOString()
        });

        console.log('✅ Guidance frameworks saved successfully');
        
        // Navigate to Phase 5 (Synthesis Instructions)
        navigate('/college/synthesis-instructions');
      }
    } catch (error) {
      console.error('Error saving guidance frameworks:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Loading question set</h2>
          <p className="text-slate-600">Preparing guidance generation...</p>
        </div>
      </div>
    );
  }

  // Show loading state while data is being fetched
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading Phase 4 data...</p>
        </div>
      </div>
    );
  }

  // Only validate after loading is complete
  if (!contextSummary || questionSet.length < 8) {
    console.log('🔍 DynamicGuidanceGenerator: Validation failed - contextSummary length:', contextSummary?.length, 'questionSet length:', questionSet.length);
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageCircle className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-4">Question Set Required</h2>
          <p className="text-slate-600 mb-6">
            You need to complete Phase 3 (Question Set Generation) with at least 8 questions before creating guidance frameworks.
          </p>
          <p className="text-sm text-slate-500 mb-6">
            Current: {questionSet.length} questions, Context: {contextSummary?.length || 0} characters
          </p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/college/question-set-generator')}
              className="w-full px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 font-medium"
            >
              Complete Phase 3: Question Set
            </button>
            <button
              onClick={() => navigate('/college')}
              className="w-full px-6 py-3 text-slate-600 hover:text-slate-900"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate('/college/question-set-generator')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Question Set
            </button>
          </div>
          
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 rounded-full border border-slate-700 mb-4">
              <Zap className="w-5 h-5 text-white" />
              <span className="text-sm font-medium text-white">Phase 4: Dynamic Guidance</span>
            </div>
            <h1 className="text-4xl font-bold text-slate-900 mb-4">
              Dynamic Guidance Generator
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Create guidance frameworks that will enable API AI to generate personalized follow-up questions 
              and synthesize compelling final results.
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto space-y-8">
          {/* Context & Questions Review */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Assessment Foundation
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-slate-900 mb-2">Strategic Context</h3>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-slate-700 text-sm leading-relaxed">
                    {contextSummary.substring(0, 200)}...
                  </p>
                </div>
              </div>
              
              <div>
                <h3 className="font-medium text-slate-900 mb-2">Initial Questions (1-8)</h3>
                <div className="bg-slate-50 rounded-lg p-3 max-h-48 overflow-y-auto">
                  <div className="space-y-2">
                    {questionSet.map((question, index) => (
                      <div key={index} className="text-sm">
                        <span className="font-medium text-slate-600">{index + 1}.</span>
                        <span className="text-slate-700 ml-2">{question.question}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Dynamic Question Guidance */}
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Brain className="w-5 h-5" />
                Dynamic Question Guidance (Questions 9-15)
              </h2>
              
              <p className="text-slate-600 mb-4">
                Create instructions for API AI to generate personalized follow-up questions 9-15 
                based on how the student responds to questions 1-8.
              </p>

              {/* Copy Prompt */}
              <div className="bg-slate-50 rounded-lg p-4 mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-slate-700">Dynamic Question Guidance Prompt</span>
                  <button
                    onClick={copyPromptToClipboard}
                    className={`flex items-center gap-2 px-3 py-1 rounded text-sm font-medium ${
                      copySuccess === 'dynamic_copied' 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : copySuccess === 'dynamic_failed'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                    }`}
                  >
                    {copySuccess === 'dynamic_copied' ? (
                      <>
                        <CheckCircle className="w-3 h-3" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" />
                        Copy Prompt
                      </>
                    )}
                  </button>
                </div>
                <div className="bg-white rounded border p-3 max-h-48 overflow-y-auto">
                  <pre className="text-xs text-slate-600 whitespace-pre-wrap">
                    {generateDynamicQuestionPrompt()}
                  </pre>
                </div>
              </div>

              {/* Paste Guidance */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Dynamic Question Guidance (from Claude)
                </label>
                <textarea
                  value={dynamicQuestionGuidance}
                  onChange={(e) => setDynamicQuestionGuidance(e.target.value)}
                  placeholder="Paste the DYNAMIC_QUESTION_GUIDANCE from Claude here..."
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 resize-none"
                  rows="10"
                />
              </div>
            </div>
          </div>

          {/* Guidance Refinement Options */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <Target className="w-5 h-5" />
                Guidance Refinement Options
              </h2>
              <button
                onClick={generateGuidanceRefinements}
                className="flex items-center gap-1 px-2 py-1 text-xs text-slate-600 hover:text-slate-900"
              >
                <RefreshCw className="w-3 h-3" />
                Generate Options
              </button>
            </div>
            
            <div className="space-y-2">
              {[0, 1, 2, 3].map((index) => (
                <input
                  key={index}
                  type="text"
                  value={guidanceRefinementOptions[index] || ''}
                  onChange={(e) => {
                    const newOptions = [...guidanceRefinementOptions];
                    newOptions[index] = e.target.value;
                    setGuidanceRefinementOptions(newOptions);
                  }}
                  placeholder={`Refinement option ${index + 1} for guidance frameworks...`}
                  className="w-full px-3 py-2 border border-slate-200 rounded focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                />
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <button
              onClick={() => navigate('/college/question-set-generator')}
              className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Question Set
            </button>

            <button
              onClick={saveGuidanceFrameworks}
              disabled={!dynamicQuestionGuidance.trim() || saving}
              className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save & Continue to Framework Customization
                </>
              )}
            </button>
          </div>

          {!dynamicQuestionGuidance.trim() && (
            <div className="text-center">
              <p className="text-slate-500 text-sm">
                Dynamic question guidance is required to proceed to Framework Customization
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DynamicGuidanceGenerator;